import React, { useState } from 'react';
import { TutorialCard } from '../components/TutorialCard';
import { SearchIcon, FilterIcon, SlidersIcon, CreditCardIcon, LayoutGridIcon, FileTextIcon, UsersIcon, ArrowLeftRightIcon, ShoppingBagIcon, SettingsIcon, GraduationCapIcon, BarChartIcon, CalendarIcon, TerminalIcon, SmartphoneIcon, BanknoteIcon, GlobeIcon } from 'lucide-react';
// Mock tutorial data
const tutorialData = [{
  id: 1,
  title: 'Getting Started with Payment Requests',
  description: 'Learn how to create and manage payment requests for your customers.',
  category: 'Basics',
  duration: '5 min',
  completed: true,
  completedAt: '2023-11-15T14:30:00',
  icon: <LayoutGridIcon size={24} className="text-blue-500" data-id="element-6188" />
}, {
  id: 2,
  title: 'Setting Up Your Virtual Terminal',
  description: 'Configure your virtual terminal for accepting card payments.',
  category: 'Terminals',
  duration: '8 min',
  completed: true,
  completedAt: '2023-11-20T10:15:00',
  icon: <BanknoteIcon size={24} className="text-purple-500" data-id="element-6189" />
}, {
  id: 3,
  title: 'Creating Your First Invoice',
  description: 'Learn how to create, customize and send professional invoices.',
  category: 'Invoicing',
  duration: '6 min',
  completed: false,
  completedAt: null,
  icon: <FileTextIcon size={24} className="text-green-500" data-id="element-6190" />
}, {
  id: 4,
  title: 'Managing Customer Profiles',
  description: 'Organize and manage your customer information efficiently.',
  category: 'Customers',
  duration: '4 min',
  completed: true,
  completedAt: '2023-12-01T09:45:00',
  icon: <UsersIcon size={24} className="text-orange-500" data-id="element-6191" />
}, {
  id: 5,
  title: 'Understanding Transaction Reports',
  description: 'Learn how to view and interpret your transaction reports.',
  category: 'Reports',
  duration: '10 min',
  completed: false,
  completedAt: null,
  icon: <BarChartIcon size={24} className="text-red-500" data-id="element-6192" />
}, {
  id: 6,
  title: 'Setting Up Physical Terminals',
  description: 'Configure and manage your physical POS terminals.',
  category: 'Terminals',
  duration: '12 min',
  completed: false,
  completedAt: null,
  icon: <TerminalIcon size={24} className="text-indigo-500" data-id="element-6193" />
}, {
  id: 7,
  title: 'Configuring SoftPOS on Mobile Devices',
  description: 'Turn your smartphone into a payment terminal with SoftPOS.',
  category: 'Terminals',
  duration: '7 min',
  completed: false,
  completedAt: null,
  icon: <SmartphoneIcon size={24} className="text-teal-500" data-id="element-6194" />
}, {
  id: 8,
  title: 'Connecting Third-Party Applications',
  description: 'Integrate your payment system with other business tools.',
  category: 'Integration',
  duration: '15 min',
  completed: true,
  completedAt: '2023-12-10T16:20:00',
  icon: <GlobeIcon size={24} className="text-blue-600" data-id="element-6195" />
}, {
  id: 9,
  title: 'Setting Up Booking Services',
  description: 'Configure your booking system for appointments and reservations.',
  category: 'Bookings',
  duration: '9 min',
  completed: false,
  completedAt: null,
  icon: <CalendarIcon size={24} className="text-pink-500" data-id="element-6196" />
}, {
  id: 10,
  title: 'Managing Your Online Shop',
  description: 'Learn how to set up and manage your online store.',
  category: 'E-commerce',
  duration: '14 min',
  completed: true,
  completedAt: '2023-12-05T11:30:00',
  icon: <ShoppingBagIcon size={24} className="text-yellow-600" data-id="element-6197" />
}, {
  id: 11,
  title: 'Customizing Payment Settings',
  description: 'Configure payment settings to match your business needs.',
  category: 'Settings',
  duration: '6 min',
  completed: false,
  completedAt: null,
  icon: <SettingsIcon size={24} className="text-gray-600" data-id="element-6198" />
}, {
  id: 12,
  title: 'Advanced Transaction Management',
  description: 'Learn advanced techniques for managing complex transactions.',
  category: 'Advanced',
  duration: '18 min',
  completed: false,
  completedAt: null,
  icon: <ArrowLeftRightIcon size={24} className="text-purple-600" data-id="element-6199" />
}];
// Available categories for filtering
const categories = ['All', 'Basics', 'Terminals', 'Invoicing', 'Customers', 'Reports', 'Integration', 'Bookings', 'E-commerce', 'Settings', 'Advanced'];
export function Tutorials() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [filter, setFilter] = useState('all'); // 'all', 'completed', 'incomplete'
  // Filter tutorials based on search, category, and completion status
  const filteredTutorials = tutorialData.filter(tutorial => {
    const matchesSearch = tutorial.title.toLowerCase().includes(searchTerm.toLowerCase()) || tutorial.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || tutorial.category === selectedCategory;
    const matchesFilter = filter === 'all' || filter === 'completed' && tutorial.completed || filter === 'incomplete' && !tutorial.completed;
    return matchesSearch && matchesCategory && matchesFilter;
  });
  // Calculate progress statistics
  const totalTutorials = tutorialData.length;
  const completedTutorials = tutorialData.filter(t => t.completed).length;
  const progressPercentage = Math.round(completedTutorials / totalTutorials * 100);
  return <div className="space-y-6" data-id="element-6200">
      {/* Progress overview */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-6201">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4" data-id="element-6202">
          <div data-id="element-6203">
            <h2 className="text-xl font-semibold text-gray-800 flex items-center" data-id="element-6204">
              <GraduationCapIcon size={20} className="mr-2 text-blue-600" data-id="element-6205" />
              Learning Progress
            </h2>
            <p className="text-gray-600 mt-1" data-id="element-6206">
              Track your platform learning journey
            </p>
          </div>
          <div className="mt-2 sm:mt-0 bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-sm font-medium" data-id="element-6207">
            {completedTutorials} of {totalTutorials} tutorials completed
          </div>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2.5 mb-1" data-id="element-6208">
          <div className="bg-blue-600 h-2.5 rounded-full transition-all duration-500 ease-out" style={{
          width: `${progressPercentage}%`
        }} data-id="element-6209"></div>
        </div>
        <div className="text-right text-sm text-gray-600" data-id="element-6210">
          {progressPercentage}% complete
        </div>
      </div>
      {/* Search and filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-6211">
        <div className="flex flex-col md:flex-row gap-4" data-id="element-6212">
          <div className="relative flex-grow" data-id="element-6213">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-6214">
              <SearchIcon size={16} className="text-gray-400" data-id="element-6215" />
            </div>
            <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="Search tutorials..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-6216" />
          </div>
          <div className="flex space-x-4" data-id="element-6217">
            <div className="relative" data-id="element-6218">
              <select className="appearance-none bg-white border border-gray-300 rounded-md pl-3 pr-8 py-2 focus:ring-blue-500 focus:border-blue-500" value={selectedCategory} onChange={e => setSelectedCategory(e.target.value)} data-id="element-6219">
                {categories.map(category => <option key={category} value={category} data-id="element-6220">
                    {category}
                  </option>)}
              </select>
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" data-id="element-6221">
                <FilterIcon size={16} className="text-gray-400" data-id="element-6222" />
              </div>
            </div>
            <div className="relative" data-id="element-6223">
              <select className="appearance-none bg-white border border-gray-300 rounded-md pl-3 pr-8 py-2 focus:ring-blue-500 focus:border-blue-500" value={filter} onChange={e => setFilter(e.target.value)} data-id="element-6224">
                <option value="all" data-id="element-6225">All tutorials</option>
                <option value="completed" data-id="element-6226">Completed</option>
                <option value="incomplete" data-id="element-6227">Not completed</option>
              </select>
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" data-id="element-6228">
                <SlidersIcon size={16} className="text-gray-400" data-id="element-6229" />
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Tutorials grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-6230">
        {filteredTutorials.map(tutorial => <TutorialCard key={tutorial.id} id={tutorial.id} title={tutorial.title} description={tutorial.description} category={tutorial.category} duration={tutorial.duration} completed={tutorial.completed} completedAt={tutorial.completedAt} icon={tutorial.icon} data-id="element-6231" />)}
        {filteredTutorials.length === 0 && <div className="col-span-3 py-16 flex flex-col items-center justify-center text-center" data-id="element-6232">
            <GraduationCapIcon size={48} className="text-gray-300 mb-4" data-id="element-6233" />
            <h3 className="text-lg font-medium text-gray-700" data-id="element-6234">
              No tutorials found
            </h3>
            <p className="text-gray-500 mt-1" data-id="element-6235">
              Try adjusting your search or filters
            </p>
          </div>}
      </div>
    </div>;
}